import { useMemo } from "react"

import { sanitizeData } from "@l2r-front/l2r-geodata"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { GeoJSONSourceSpecification, Source, useMapDispatchContext } from "@l2r-front/l2r-map"
import { useNetworkReferential } from "@l2r-front/l2r-networks"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { useRoadsideStateContext } from "../../contexts/RoadsideContext"
import { useRoadsideHomogenousZones } from "../../hooks/queries/useRoadsideHomogenousZones"

type RoadRoadsideHomogenousZonesSourceProps = GeoJSONSourceSpecification & {
    id?: string,
    road?: string,
    children?: React.ReactNode,
}

export function RoadRoadsideHomogenousZonesSource(props: RoadRoadsideHomogenousZonesSourceProps) {
    const {
        road,
        ...sourceProps
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)
    const { setError: setMapError } = useMapDispatchContext()
    const { filters } = useRoadsideStateContext()

    const { data: homogenousZones, isLoading: isLoadingHomogenousZones } = useRoadsideHomogenousZones({
        as_geojson: true,
        linear_location_road: road,
        ...filters?.conditions?.length && { condition: filters.conditions },
        ...filters?.type && { roadside_inventory_type__ancestor: filters.type },
    }, {
        enabled: !!road,
        onError: () => setMapError(true),
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.roadsideSource.error"),
    })

    const { data: referential, isLoading: isLoadingReferential } = useNetworkReferential({
        as_geojson: true,
        linear_location_road: road,
    }, {
        enabled: !!road,
        onError: () => setMapError(true),
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.roadsideSource.error"),
    })

    // Need to copy id into properties because of that mapbox issue: 
    // link:https://github.com/mapbox/mapbox-gl-js/issues/2716
    const sanitizedData = useMemo(() => {
        if ((isLoadingHomogenousZones || !homogenousZones) || (isLoadingReferential || !referential?.features?.length)) {
            return null
        }

        (referential?.features || []).forEach(feature => {
            homogenousZones?.features.push(feature)
        })

        const newData = sanitizeData(homogenousZones)
        if (homogenousZones?.features?.length) {
            const newFeatures = []
            if (newData) {
                newData.features.reduce((acc, feature) => {
                    const positiveValue = feature.properties["inventoryStates"]?.["+"]?.reduce((acc, state) => Math.max(acc, state.condition), 0)
                    const negativeValue = feature.properties["inventoryStates"]?.["-"]?.reduce((acc, state) => Math.max(acc, state.condition), 0)

                    if (positiveValue !== undefined || negativeValue === undefined) {
                        acc.push({
                            ...feature,
                            properties: {
                                ...feature.properties,
                                condition: positiveValue,
                                side: "RIGHT",
                            },
                        })
                    }

                    if (negativeValue !== undefined || positiveValue === undefined) {
                        acc.push({
                            ...feature,
                            properties: {
                                ...feature.properties,
                                condition: negativeValue,
                                side: "LEFT",
                            },
                            geometry: {
                                ...feature.geometry,
                                coordinates: feature.geometry.type === "LineString"
                                    ? (Array.isArray(feature.geometry.coordinates) ? feature.geometry.coordinates.slice().reverse() : feature.geometry.coordinates)
                                    : (Array.isArray(feature.geometry.coordinates)
                                        ? feature.geometry.coordinates.map(coords => Array.isArray(coords) ? coords.slice().reverse() : coords)
                                        : feature.geometry.coordinates),
                            },
                        })
                    }

                    return acc
                }, newFeatures)
                newData.features = newFeatures
            }
        }
        return newData as GeoJSON.FeatureCollection
    }, [isLoadingHomogenousZones, isLoadingReferential, homogenousZones, referential])

    return (
        <Source {...sourceProps} type="geojson" data={sanitizedData} tolerance={.001} />
    )
}